import React, { useState, useEffect, useMemo } from 'react';
import { salesService } from '../../services/salesService';
import CreateOrder from './CreateOrder';
import FinalizeSale from './FinalizeSale';
import SalesDashboard from './SalesDashboard';
import { Tab } from '@headlessui/react';
import {
  ShoppingCartIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  PencilIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { PDFDownloadLink } from '@react-pdf/renderer';
import DeliveryList from './DeliveryList';
import { contactsService } from '../../services/contactsService';
import EditOrderDetailsModal from './EditOrderDetailsModal';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from '@tanstack/react-table';
import PendingPayments from './PendingPayments';
import { ErrorBoundary } from 'react-error-boundary';
import ConfirmationDialog from '../common/ConfirmationDialog';
import DataTable from '../common/DataTable';
import SelectVehicleModal from './SelectVehicleModal';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';

export default function SalesManagement() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showFinalize, setShowFinalize] = useState(false);
  const [customerDetails, setCustomerDetails] = useState({});
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
  const [orderToEditDetails, setOrderToEditDetails] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [showAssignDelivery, setShowAssignDelivery] = useState(false);
  const [showVehicleSelect, setShowVehicleSelect] = useState(false);
  const [businessDetails, setBusinessDetails] = useState(null);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const [todaysOrders, pendingOrders, ordersWithBalance] = await Promise.all([
        salesService.getTodaysOrders(),
        salesService.getAllPendingOrders(),
        salesService.getOrdersWithBalance()
      ]);
      
      console.log('Fetched orders:', {
        todaysOrders,
        pendingOrders,
        ordersWithBalance
      });
      
      // Combine and deduplicate orders
      const combinedOrders = [...todaysOrders];
      [...pendingOrders, ...ordersWithBalance].forEach(order => {
        if (!combinedOrders.find(o => o.id === order.id)) {
          combinedOrders.push(order);
        }
      });
      
      console.log('Combined orders:', combinedOrders);
      setOrders(combinedOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetails = async (orders) => {
    const newDetails = {};
    const fetchPromises = orders
      .filter(order => order.customerId && !customerDetails[order.customerId])
      .map(async order => {
        try {
          const customerData = await contactsService.getContact(order.customerId);
          newDetails[order.customerId] = customerData;
        } catch (error) {
          console.error(`Error fetching customer details for ID ${order.customerId}:`, error);
          newDetails[order.customerId] = null;
        }
      });

    await Promise.all(fetchPromises);
    if (Object.keys(newDetails).length > 0) {
      setCustomerDetails(prev => ({ ...prev, ...newDetails }));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [todaysOrders, pendingOrders, ordersWithBalance] = await Promise.all([
          salesService.getTodaysOrders(),
          salesService.getAllPendingOrders(),
          salesService.getOrdersWithBalance()
        ]);
        
        // Fetch business details
        const businessDoc = await getDoc(doc(db, 'settings', 'business'));
        if (businessDoc.exists()) {
          setBusinessDetails(businessDoc.data());
        }
        
        console.log('Fetched orders:', {
          todaysOrders,
          pendingOrders,
          ordersWithBalance
        });
        
        // Combine and deduplicate orders
        const combinedOrders = [...todaysOrders];
        [...pendingOrders, ...ordersWithBalance].forEach(order => {
          if (!combinedOrders.find(o => o.id === order.id)) {
            combinedOrders.push(order);
          }
        });
        
        setOrders(combinedOrders);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (orders.length > 0) {
      fetchCustomerDetails(orders);
    }
  }, [orders]);

  const handleOrderCreated = () => {
    fetchOrders();
  };

  const handleSaleFinalized = () => {
    setShowFinalize(false);
    fetchOrders();
  };

  const getCompletedOrders = () => {
    return orders.filter(order => order.status === 'completed');
  };

  const getPendingOrders = () => {
    return orders.filter(order => order.status === 'pending');
  };

  const getTotalSales = () => {
    return getCompletedOrders().reduce((sum, order) => {
      return sum + (order.totalAmount || 0);
    }, 0);
  };

  const getAverageOrderValue = () => {
    const completedOrders = getCompletedOrders();
    if (completedOrders.length === 0) return 0;
    return getTotalSales() / completedOrders.length;
  };

  const getPendingPayments = () => {
    return getCompletedOrders().reduce((sum, order) => {
      return sum + (order.remainingBalance || 0);
    }, 0);
  };

  const handleDeleteOrder = (order) => {
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (!orderToDelete?.id) {
        console.error('No order ID provided for deletion');
        return;
      }
      
      await salesService.deleteOrder(orderToDelete.id);
      await fetchOrders();
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
      
      // Optionally add success notification
      // toast.success('Order deleted successfully');
    } catch (error) {
      console.error('Error deleting order:', error);
      // Optionally add error notification
      // toast.error('Failed to delete order');
    }
  };

  const handleOrderDetailsUpdated = (updatedOrder) => {
    setOrders(orders.map(order => 
      order.id === updatedOrder.id ? updatedOrder : order
    ));
    setShowEditDetailsModal(false);
  };

  const handleBack = () => {
    setShowFinalize(false);
  };

  const tabs = [
    { name: 'Dashboard', icon: ChartBarIcon },
    { name: 'Pending Orders', icon: ClipboardDocumentListIcon },
    { name: 'Pending Payments', icon: CurrencyDollarIcon },
  ];

  // Define columns configuration
  const columns = [
    {
      header: 'Customer',
      accessorKey: 'customerName',
    },
    {
      header: 'Items',
      accessorKey: 'items',
      cell: (info) => {
        const items = info.getValue();
        return (
          <div className="max-w-[200px] sm:max-w-none">
            {items.map(item => 
              `${item.vegetable} (${item.estimatedQuantity}kg)`
            ).join(', ')}
          </div>
        );
      },
    },
    {
      header: 'Vehicle',
      accessorKey: 'vehicle',
      cell: (info) => {
        const vehicle = info.getValue();
        return vehicle ? (
          <div className="text-sm">
            <div className="font-medium text-gray-900 dark:text-gray-100">
              {vehicle.registrationNumber}
            </div>
            <div className="text-gray-500 dark:text-gray-400">
              {vehicle.driverName}
            </div>
          </div>
        ) : (
          <span className="text-gray-400 dark:text-gray-500 text-sm">
            Not assigned
          </span>
        );
      },
    },
    {
      header: 'Actions',
      id: 'actions',
      cell: (info) => (
        <div className="flex flex-col sm:flex-row gap-2 min-w-[120px]">
          {info.row.original.status === 'pending' && (
            <>
              <button
                onClick={() => {
                  setSelectedOrder(info.row.original);
                  setShowFinalize(true);
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded 
                  text-primary-700 bg-primary-100 hover:bg-primary-200 
                  dark:text-primary-400 dark:bg-primary-900/50 dark:hover:bg-primary-900"
              >
                <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                Finalize
              </button>
              <button
                onClick={() => {
                  setOrderToEditDetails(info.row.original);
                  setShowEditDetailsModal(true);
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded 
                  text-primary-700 bg-primary-100 hover:bg-primary-200 
                  dark:text-primary-400 dark:bg-primary-900/50 dark:hover:bg-primary-900"
              >
                <PencilIcon className="h-4 w-4 mr-1" />
                Edit
              </button>
              <button
                onClick={() => handleDeleteOrder(info.row.original)}
                className="w-full sm:w-auto inline-flex items-center justify-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded 
                  text-red-700 bg-red-100 hover:bg-red-200 
                  dark:text-red-400 dark:bg-red-900/50 dark:hover:bg-red-900"
              >
                <TrashIcon className="h-4 w-4 mr-1" />
                Delete
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  // Initialize table
  const pendingOrders = useMemo(() => getPendingOrders(), [orders]);

  const table = useReactTable({
    data: pendingOrders,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="relative mb-24">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Sales Management</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            Manage your daily sales, customer orders, and view analytics
          </p>
        </div>
      </div>

      <button
        onClick={() => setShowCreateOrder(true)}
        className="fixed lg:bottom-6 bottom-20 right-6 p-4 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 
          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 z-40
          flex items-center justify-center group"
      >
        <ShoppingCartIcon className="h-6 w-6" />
        <span className="hidden md:block ml-2 mr-1 group-hover:mr-2 transition-all duration-200">
          Create Order
        </span>
      </button>

      <CreateOrder 
        isOpen={showCreateOrder}
        onClose={() => setShowCreateOrder(false)}
        onOrderCreated={() => {
          handleOrderCreated();
          setShowCreateOrder(false);
        }}
      />

      <FinalizeSale 
        isOpen={showFinalize}
        onClose={() => setShowFinalize(false)}
        order={selectedOrder}
        onSaleFinalized={handleSaleFinalized}
      />

      <div className="mt-8">
        <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
          <Tab.List className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <Tab
                key={tab.name}
                className={({ selected }) => `
                  ${selected
                    ? 'bg-primary-600 text-white dark:bg-primary-500 dark:text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  } 
                  rounded-full px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-700 
                  transition-colors duration-150 ease-in-out flex items-center space-x-2
                `}
              >
                <tab.icon className="h-5 w-5" />
                <span>{tab.name}</span>
              </Tab>
            ))}
          </Tab.List>

          <Tab.Panels className="mt-4">
            <Tab.Panel>
              <SalesDashboard 
                orders={getCompletedOrders()}
                totalSales={getTotalSales()}
                averageOrderValue={getAverageOrderValue()}
                pendingPayments={getPendingPayments()}
                totalOrders={getCompletedOrders().length}
              />
            </Tab.Panel>
            <Tab.Panel>
              <ErrorBoundary fallback={<div>Something went wrong loading the orders.</div>}>
                <div>
                  <div className="flex justify-end mb-4">
                    {pendingOrders && pendingOrders.length > 0 && (
                      <button 
                        onClick={() => setShowVehicleSelect(true)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium 
                          rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 
                          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <ClipboardDocumentListIcon className="h-5 w-5 mr-2" />
                        Download Delivery List
                      </button>
                    )}
                  </div>

                  <DataTable
                    data={pendingOrders}
                    columns={columns}
                    sorting={sorting}
                    setSorting={setSorting}
                    globalFilter={globalFilter}
                    setGlobalFilter={setGlobalFilter}
                    searchPlaceholder="Search orders..."
                  />
                </div>
              </ErrorBoundary>
            </Tab.Panel>
            <Tab.Panel>
              <PendingPayments 
                orders={orders.filter(order => order.status === 'completed' && Number(order.remainingBalance) > 0)}
                customerDetails={customerDetails}
              />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>

      {showEditDetailsModal && (
        <EditOrderDetailsModal
          isOpen={showEditDetailsModal}
          onClose={() => setShowEditDetailsModal(false)}
          order={orderToEditDetails}
          onOrderUpdated={handleOrderDetailsUpdated}
        />
      )}

      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Order"
        message={`Are you sure you want to delete the order for ${orderToDelete?.customerName}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      <SelectVehicleModal
        isOpen={showVehicleSelect}
        onClose={() => setShowVehicleSelect(false)}
        orders={pendingOrders}
        customerDetails={customerDetails}
        businessDetails={businessDetails}
      />

    </div>
  );
}